import {
  format,
  differenceInDays,
  parseISO,
} from "date-fns";
import { getDistance } from "geolib";
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ACCEPT_STRING,
  IMAGE_UPLOAD_EXTENSIONS,
  IMAGE_UPLOAD_MIME_TYPES,
  LIVE_PHOTO_UPLOAD_ACCEPT,
  LIVE_PHOTO_UPLOAD_ACCEPT_STRING,
  SHORT_VIDEO_UPLOAD_ACCEPT,
  SHORT_VIDEO_UPLOAD_ACCEPT_STRING,
  SHORT_VIDEO_UPLOAD_LIMITS,
  SHORT_VIDEO_UPLOAD_MESSAGES,
  SHORT_VIDEO_UPLOAD_EXTENSIONS,
  SHORT_VIDEO_UPLOAD_MIME_TYPES,
} from "@/constants/video-upload";
import { buildCurrentUserProfilePath } from "@/constants/routes";

export {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ACCEPT_STRING,
  IMAGE_UPLOAD_EXTENSIONS,
  IMAGE_UPLOAD_MIME_TYPES,
  LIVE_PHOTO_UPLOAD_ACCEPT,
  LIVE_PHOTO_UPLOAD_ACCEPT_STRING,
  SHORT_VIDEO_UPLOAD_ACCEPT,
  SHORT_VIDEO_UPLOAD_ACCEPT_STRING,
  SHORT_VIDEO_UPLOAD_EXTENSIONS,
  SHORT_VIDEO_UPLOAD_LIMITS,
  SHORT_VIDEO_UPLOAD_MESSAGES,
  SHORT_VIDEO_UPLOAD_MIME_TYPES,
};

export function getDaysDifference(eventDate) {
  const now = new Date();
  const event = new Date(eventDate);

  const difference = differenceInDays(event, now);

  if (difference > 0) {
    return `${difference} days left`;
  } else if (difference < 0) {
    return `${Math.abs(difference)} days ago`;
  } else {
    return "Today";
  }
}

export function timeAgo(createdAt) {
  const now = new Date();
  const past = new Date(createdAt);
  const diffInSeconds = Math.floor((now - past) / 1000);

  const timeIntervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "week", seconds: 604800 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for (const interval of timeIntervals) {
    const timeDifference = Math.floor(diffInSeconds / interval.seconds);
    if (timeDifference >= 1) {
      const label = interval.label + (timeDifference > 1 ? "s" : "");
      return `${timeDifference} ${label} ago`;
    }
  }

  return "Just now";
}

export function timeAgoInKorean(createdAt) {
  const now = new Date();
  const past = new Date(createdAt);
  const diffInSeconds = Math.floor((now - past) / 1000);

  const timeIntervals = [
    { label: "년", seconds: 31536000 },
    { label: "개월", seconds: 2592000 },
    { label: "주", seconds: 604800 },
    { label: "일", seconds: 86400 },
    { label: "시간", seconds: 3600 },
    { label: "분", seconds: 60 },
    { label: "초", seconds: 1 },
  ];

  for (const interval of timeIntervals) {
    const timeDifference = Math.floor(diffInSeconds / interval.seconds);
    if (timeDifference >= 1) {
      return `${timeDifference}${interval.label} 전`;
    }
  }

  return "방금 전";
}

export const queryString = () => {
  let query = window.location.search.substr(1);
  let resultval = {};
  query.split("&").forEach((part) => {
    let item = part.split("=");
    resultval[item[0]] = decodeURIComponent(item[1]);
  });
  return resultval;
};

const getUploadFileExtension = (file) => {
  const name = file?.name || "";
  const dotIndex = name.lastIndexOf(".");
  return dotIndex >= 0 ? name.slice(dotIndex).toLowerCase() : "";
};

export function isAllowedUploadFile(file, { mimeTypes, extensions }) {
  if (!file) return false;
  return (
    mimeTypes.includes(file.type) &&
    extensions.includes(getUploadFileExtension(file))
  );
}

export const isAllowedImageUploadFile = (file) =>
  isAllowedUploadFile(file, {
    mimeTypes: IMAGE_UPLOAD_MIME_TYPES,
    extensions: IMAGE_UPLOAD_EXTENSIONS,
  });

export const isAllowedShortVideoUploadFile = (file) =>
  isAllowedUploadFile(file, {
    mimeTypes: SHORT_VIDEO_UPLOAD_MIME_TYPES,
    extensions: SHORT_VIDEO_UPLOAD_EXTENSIONS,
  });

export function getVideoFileMetadata(file) {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined" || typeof URL === "undefined") {
      reject(new Error(SHORT_VIDEO_UPLOAD_MESSAGES.invalid));
      return;
    }

    const videoElement = document.createElement("video");
    const objectUrl = URL.createObjectURL(file);

    const cleanup = () => {
      URL.revokeObjectURL(objectUrl);
      videoElement.removeAttribute("src");
      videoElement.load();
    };

    videoElement.preload = "metadata";
    videoElement.onloadedmetadata = () => {
      const metadata = {
        duration: videoElement.duration,
        width: videoElement.videoWidth,
        height: videoElement.videoHeight,
      };
      cleanup();
      resolve(metadata);
    };
    videoElement.onerror = () => {
      cleanup();
      reject(new Error(SHORT_VIDEO_UPLOAD_MESSAGES.invalid));
    };
    videoElement.src = objectUrl;
  });
}

export async function validateShortVideoUploadFile(file) {
  if (!isAllowedShortVideoUploadFile(file)) {
    return {
      valid: false,
      message: SHORT_VIDEO_UPLOAD_MESSAGES.format,
    };
  }

  if (
    !Number.isFinite(Number(file.size)) ||
    Number(file.size) <= 0 ||
    file.size > SHORT_VIDEO_UPLOAD_LIMITS.maxSizeBytes
  ) {
    return {
      valid: false,
      message: SHORT_VIDEO_UPLOAD_MESSAGES.size,
    };
  }

  try {
    const metadata = await getVideoFileMetadata(file);
    const duration = Number(metadata.duration);

    if (
      !Number.isFinite(duration) ||
      duration < SHORT_VIDEO_UPLOAD_LIMITS.minDurationSeconds ||
      duration > SHORT_VIDEO_UPLOAD_LIMITS.maxDurationSeconds
    ) {
      return {
        valid: false,
        message: SHORT_VIDEO_UPLOAD_MESSAGES.duration,
      };
    }

    return {
      valid: true,
      metadata,
      warning:
        metadata.width > metadata.height
          ? SHORT_VIDEO_UPLOAD_MESSAGES.ratio
          : null,
    };
  } catch (error) {
    return {
      valid: false,
      message: error?.message || SHORT_VIDEO_UPLOAD_MESSAGES.invalid,
    };
  }
}

export function checkVideoFile(file) {
  return (
    isAllowedShortVideoUploadFile(file) &&
    Number(file.size) > 0 &&
    file.size <= SHORT_VIDEO_UPLOAD_LIMITS.maxSizeBytes
  );
}

export function fData(inputValue) {
  if (!inputValue) return "";

  if (inputValue === 0) return "0 Bytes";

  const units = ["bytes", "Kb", "Mb", "Gb", "Tb", "Pb", "Eb", "Zb", "Yb"];

  const decimal = 2;

  const baseValue = 1024;

  const number = Number(inputValue);

  const index = Math.floor(Math.log(number) / Math.log(baseValue));

  const fm = `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${
    units[index]
  }`;

  return fm;
}

export function fileTypeByUrl(fileUrl = "") {
  return (fileUrl && fileUrl.split(".").pop()) || "";
}

export function fileNameByUrl(fileUrl) {
  return fileUrl.split("/").pop();
}

export function fileData(file) {
  // Url
  if (typeof file === "string") {
    return {
      key: file,
      preview: file,
      name: fileNameByUrl(file),
      type: fileTypeByUrl(file),
    };
  }

  // File
  return {
    key: file.preview,
    name: file.name,
    size: file.size,
    path: file.path,
    type: file.type,
    preview: file.preview,
    lastModified: file.lastModified,
    lastModifiedDate: file.lastModifiedDate,
  };
}

export const dataURLtoBlob = (dataURL) => {
  const arr = dataURL.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

export const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);

  const options = {
    month: "long", // Full month name
    day: "2-digit", // 2-digit day
    year: "numeric", // 4-digit year
    hour: "2-digit", // 2-digit hour
    minute: "2-digit", // 2-digit minute
    hour12: false, // Use 24-hour format
  };

  return new Intl.DateTimeFormat("en-US", options).format(date);
};

export const formatScheduleRelease = (dateString) => {
  try {
    if (!dateString) return null;
    const date = new Date(dateString);

    // Formatting options for time and date
    const timeOptions = {
      hour: "2-digit", // 2-digit hour
      minute: "2-digit", // 2-digit minute
      hour12: false, // Use 24-hour format
    };

    const dateOptions = {
      month: "long", // Full month name
      day: "numeric", // Numeric day
      year: "numeric", // Full year
    };

    const formattedTime = new Intl.DateTimeFormat("en-US", timeOptions).format(
      date
    );
    const formattedDate = new Intl.DateTimeFormat("en-US", dateOptions).format(
      date
    );

    return `Schedule Release at ${formattedTime} on ${formattedDate}`;
  } catch (error) {
    console.log(error?.message);
    return null;
  }
};

export function removeDuplicates(arr) {
  const seen = new Set();
  return arr?.filter((item) => {
    const duplicate = seen?.has(item?.label);
    seen?.add(item?.label);
    return !duplicate;
  });
}

export function getShortDescription(text, wordLimit) {
  const words = text.split(" ");
  return words.length > wordLimit
    ? words.slice(0, wordLimit).join(" ") + "..."
    : text;
}

export function convertTimestampToLocal(timestamp) {
  // Create a Date object from the timestamp
  const date = new Date(timestamp);

  // Get the day, month, year, hours, minutes, and seconds
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  // Format the date and time as dd/mm/yyyy HH:MM:SS
  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
}

export function getLastSevenProperties(obj) {
  // Get the keys of the object as an array
  const keys = Object.keys(obj);

  // Extract the last seven keys
  const lastSevenKeys = keys.slice(-7);

  // Create a new object with only the last seven properties
  const lastSevenProperties = {};
  lastSevenKeys.forEach((key) => {
    lastSevenProperties[key] = obj[key];
  });

  return lastSevenProperties;
}

export const structureComments = (comments) => {
  const commentMap = {};
  // Create a map with comment ids as keys
  comments.forEach((comment) => {
    comment.subComments = [];
    commentMap[comment.id] = comment;
  });
  // Create an array to store nested comments
  const nestedComments = [];
  // Iterate again to nest sub-comments under their parent comments
  comments.forEach((comment) => {
    if (comment.parentCommentId) {
      if (commentMap[comment.parentCommentId]) {
        // Push the comment as a sub-comment of its parent
        commentMap[comment.parentCommentId].subComments.push(comment);
      }
    } else {
      nestedComments.push(comment);
    }
  });
  // Ensure only one level of nesting by flattening sub-comments
  const flattenSubComments = (comment) => {
    comment.subComments.forEach((subComment) => {
      flattenSubComments(subComment);
      comment.subComments = [...comment.subComments, ...subComment.subComments];
      subComment.subComments = [];
    });
  };
  nestedComments.forEach(flattenSubComments);
  return nestedComments;
};

export const convertDateToDDMM = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${day}/${month}`;
};

export const convertDateToCustomFormat = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString); // Create a Date object from the input string

  const day = date.getDate(); // Get the day of the month
  const month = date.toLocaleString("default", { month: "short" }); // Get the abbreviated month name (e.g., "Nov")

  return `${day} ${month}`;
};

export const formatDistance = (valueInMeters) => {
  if (valueInMeters < 1000) {
    // Round off to nearest whole number for values less than 1km
    return `${Math.round(valueInMeters)}m`;
  } else {
    // For values equal to or more than 1km, format as kilometers
    const valueInKilometers = valueInMeters / 1000;
    return `${valueInKilometers.toFixed(1)}km`;
  }
};

// export const formatChatTimeStamp = (timestamp) => {
//   const date = new Date(timestamp);

//   // Get the day name
//   const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });

//   // Format the date and time
//   const day = date.getDate();
//   const month = date.toLocaleDateString('en-US', { month: 'short' });
//   const year = date.getFullYear();
//   const hours = date.getHours().toString().padStart(2, '0');
//   const minutes = date.getMinutes().toString().padStart(2, '0');

//   return `${dayName} ${day}, ${month} ${year} ${hours}:${minutes}`;
// };

export const formatChatTimeStamp = (timestamp) => {
  const now = new Date();

  // Get the user's language preference from localStorage
  const userLocale = localStorage.getItem("lang") || "en";

  // Ensure timestamp is a Date object
  const date =
    typeof timestamp === "string" ? parseISO(timestamp) : new Date(timestamp);

  // Calculate the difference in milliseconds
  const difference = now - date;

  if (difference < 60 * 1000) {
    // Less than 1 minute ago
    return userLocale === "ko" ? "방금 전" : "just now";
  } else if (difference < 60 * 60 * 1000) {
    // Less than 1 hour ago
    const minutes = Math.floor(difference / (60 * 1000));
    return userLocale === "ko" ? `${minutes}분 전` : `${minutes}m`;
  } else if (difference < 24 * 60 * 60 * 1000) {
    // Less than 1 day ago
    const hours = Math.floor(difference / (60 * 60 * 1000));
    return userLocale === "ko" ? `${hours}시간 전` : `${hours}h`;
  } else if (difference < 7 * 24 * 60 * 60 * 1000) {
    // Less than 1 week ago
    const days = Math.floor(difference / (24 * 60 * 60 * 1000));
    return userLocale === "ko" ? `${days}일 전` : `${days}d`;
  } else {
    // Maximum granularity: Weeks
    const weeks = Math.floor(difference / (7 * 24 * 60 * 60 * 1000));
    return userLocale === "ko" ? `${weeks}주 전` : `${weeks}w`;
  }
};

export const formatChatMessageTimeStamp = (timestamp) => {
  const date = new Date(timestamp);

  const userLocale = localStorage.getItem("lang") || "en";

  if (userLocale === "ko") {
    // Korean format (24-hour format is more common in Korean)
    return date.toLocaleTimeString("ko-KR", {
      hour: "numeric",
      minute: "numeric",
      hour12: true, // Use 12-hour format for Korean
    });
  } else {
    // English format (12-hour format with AM/PM)
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true, // Use 12-hour format for English
    });
  }
};

export const calculateDistanceBetweenCoords = (coords1, coords2) => {
  if (
    !coords1?.latitude ||
    !coords2?.latitude ||
    !coords1?.longitude ||
    !coords2?.longitude
  ) {
    return null;
  }
  const dist = getDistance(coords1, coords2);
  return dist;
};

export const showDateTag = (messages, message, index) => {
  const messageDate = new Date(message.createdAt);
  const prevMessageDate =
    index > 0 ? new Date(messages[index - 1]?.createdAt) : null;

  // Check if the date tag should be displayed
  const showDate =
    !prevMessageDate ||
    messageDate.toDateString() !== prevMessageDate.toDateString();

  return showDate;
};

export const getIdentifier = (username, userId) => {
  return buildCurrentUserProfilePath(username, userId);
};

export const processFilesToMediaObjects = (acceptedFiles) => {
  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const imageFiles = acceptedFiles.filter(isAllowedImageUploadFile);
  const videoFiles = acceptedFiles.filter(
    (file) =>
      file.type === "video/quicktime" && getUploadFileExtension(file) === ".mov"
  );

  const newMedia = [];

  imageFiles.forEach((imgFile) => {
    const baseName = imgFile.name.replace(/\.[^/.]+$/, "");
    const matchingMov = videoFiles.find((v) => v.name.startsWith(baseName));

    if (
      matchingMov &&
      imgFile.size <= MAX_FILE_SIZE &&
      matchingMov.size <= MAX_FILE_SIZE
    ) {
      newMedia.push({
        type: "livephoto",
        preview: URL.createObjectURL(imgFile),
        videoPreview: URL.createObjectURL(matchingMov),
        imageFile: imgFile,
        videoFile: matchingMov,
      });
    } else if (imgFile.size <= MAX_FILE_SIZE) {
      newMedia.push(
        Object.assign(imgFile, {
          preview: URL.createObjectURL(imgFile),
        })
      );
    }
  });

  return newMedia;
};
